import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core/common/command';
import { AIDisclosureParagraphEditType, StreamStatus } from '@/domains/disclosure/enums';
import { I18nService } from '@telecom/shared/services/i18n';
import IconEdit from '@/presentation/assets/images/icons/icon-edit.svg';
import { i18nDeferred } from '@/infrastructure/locales/deferred';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { GetContentOpt, type ApiContentResponse } from '@/infrastructure/api-functions/core-drafting-api/get-content-opt.api';
import { ParagraphTextService } from '@telecom/shared/components/spilt-editor/service/paragraph-text-service';
import type { SSEStreamService } from '@patsnap-utils/sse';

/**
 * SpiltEditorContribution
 */
@injectable()
export class AddEmbodimentContribution implements CommandContribution {
    constructor(
        @inject(I18nService)
        private _i18nService: I18nService,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(GetContentOpt)
        private readonly _getContentOpt: GetContentOpt,
        @inject(ParagraphTextService)
        private readonly _paragraphTextService: ParagraphTextService<AIDisclosureParagraphEditType>,
    ) {
    }

    private _stream: SSEStreamService<ApiContentResponse> | null = null;

    generateContent(callback: (status: StreamStatus, content?: string) => void) {
        this._stream = this._getContentOpt.getContentOpt({
            task_id: this._currentTaskService.taskId,
            operate_type: AIDisclosureParagraphEditType.ADD_EMBODIMENT,
            text: this._paragraphTextService.getParagraphText(),
            content_type: this._paragraphTextService.getParagraphTextType(),
        })
            .onMessage(data => {
                const { status, content } = data.data ?? {};
                if (status === StreamStatus.FINISH) {
                    callback(status, content);
                } else {
                    if (content.length) {
                        callback(status, content);
                    }
                }
            })
            .onError(() => {
                callback(StreamStatus.ABORTED);
            })
            .onClose(() => {
                callback(StreamStatus.FINISH);
            });

        this._stream && this._stream.start();
    }

    public stopGenerate() {
        this._stream?.close();
    }

    async registerCommands(commands: CommandRegistry): Promise<void> {
        await i18nDeferred.promise;

        commands.registerCommand({
            id: AIDisclosureParagraphEditType.ADD_EMBODIMENT,
            label: this._i18nService.t('aiDisclosure.addEmbodiment'),
            icon: IconEdit,
        }, {
            execute: () => {
                return {
                    getContentOpt: (callback: (status: StreamStatus, content?: string) => void) => this.generateContent(callback),
                    stopGenerate: () => this.stopGenerate(),
                };
            },
        });

    }
}
